import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronUpIcon, MapLayerIcon } from '@rsos/assets/icons';
import { IRP_MAP_ENABLED } from '@rsos/capstone/src/constants/permissionNames';
import BasemapTooltip from '@rsos/components/capstone/BasemapMenu/BasemapTooltip';
import Popover from '@rsos/components/capstone/base/Popover';
import {
  allSpatialLayersSelector,
  filteredDynamicLayers,
} from '@rsos/components/capstone/common/Map/SpatialLayers/selectors';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import { clearCheckedLayers } from '../../actions/spatialLayers';
import useGISHelpers from '../../hooks/useGISHelpers';
import LayerCheckbox from './LayerCheckbox';
import {
  Controls,
  AccordionTitleWrapper,
  AccordionTitle,
  CollapsedIconWrapper,
  AccordionContent,
  ControlsBottom,
  SelectedLayersWrapper,
  SelectedText,
  ClearAllText,
} from './MapLayersControl.styles';

const MapLayersControl = () => {
  const dispatch = useDispatch();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const {
    clearAllGISServices,
    gisMapLayersControlServices,
    hasEsriMapAccess,
    hasGISMapLayersControlServices,
    numActiveGISServices,
  } = useGISHelpers();
  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);
  const allSpatialLayers = useSelector(state =>
    allSpatialLayersSelector(state.spatialLayers),
  );
  const dynamicLayers = useSelector(state => state.spatialLayers.dynamicLayers);
  const disabledDynamicLayers = useSelector(
    state => state.spatialLayers.disabledDynamicLayers,
  );
  const shouldDisplayDynamicLayers = !!filteredDynamicLayers(
    dynamicLayers,
    disabledDynamicLayers,
  ).length;
  const { irp_access_token } = useSelector(state => state.sinatra.irp);
  const permissions = useSelector(state =>
    selectUserPermissionsByApp(state.sinatra.user, 'capstone'),
  );

  const isIRPmode =
    permissions && permissions.includes(IRP_MAP_ENABLED) && irp_access_token;

  const isLayerDisabled = useMemo(() => mapProvider === GOOGLE, [mapProvider]);

  const [hoveredLayerId, setHoveredLayerId] = useState(null);

  const onHandleHoverLayer = layerID => setHoveredLayerId(layerID);

  // Check whether or not to render the component on page load or when receiving
  // dynamic spatial object data when the user doesn't have access to any static
  // spatial layers. This check is necessary because the user is able to receive
  // spatial layer data if the permission is enabled, but capability is disabled
  const shouldRenderMLControls = useMemo(() => {
    return (
      dynamicLayers?.length &&
      shouldDisplayDynamicLayers &&
      (!irp_access_token || isIRPmode)
    );
  }, [
    dynamicLayers?.length,
    irp_access_token,
    isIRPmode,
    shouldDisplayDynamicLayers,
  ]);

  const shouldRenderEsriMLControls = useMemo(() => {
    return hasGISMapLayersControlServices && (!irp_access_token || isIRPmode);
  }, [hasGISMapLayersControlServices, irp_access_token, isIRPmode]);

  const toggleAccordion = () => {
    setIsCollapsed(isCollapsed => !isCollapsed);
  };

  const handleClearAll = useCallback(() => {
    setIsCollapsed(true);
    if (hasEsriMapAccess) {
      dispatch(clearAllGISServices());
    }
    dispatch(clearCheckedLayers());
  }, [hasEsriMapAccess, dispatch, clearAllGISServices]);

  const [layerCheckboxes, setLayerCheckboxes] = useState([]);

  const selectedCheckboxCount =
    numActiveGISServices + // will be 0 if hasEsriMapAccess is false
    layerCheckboxes.reduce((count, cb) => {
      if (cb?.layer?.isChecked) {
        return count + 1;
      }
      return count;
    }, 0);

  // This effect is used to set the layer checkboxes for the map layers control.
  // When the spatial layers change, the checkboxes are updated to reflect the
  // new state of the spatial layers. Based on this premise, the count of
  // selected checkboxes can be made by reducing the layerCheckboxes state,
  // see selectedCheckboxCount above.
  useEffect(() => {
    // Removes placeholder layers that are not meant to be toggled or seen by users.
    const filterPlaceholders = layer => {
      return (
        layer.key !== 'avive_aed_placeholder' &&
        layer.key !== 'avive_aed_highlight'
      );
    };

    if (hasEsriMapAccess) {
      const gisLayers = gisMapLayersControlServices
        .map(layer => ({
          id: `${layer.id}`,
          key: `${layer.id}-${layer.display_name}`,
          layer,
          isGISSupplementalLayer: !!layer.gis_service_url,
          isDisabled: isLayerDisabled,
        }))
        .sort((a, b) => b.layer?.layerIndex - a.layer?.layerIndex);

      const spatialLayers = allSpatialLayers
        .map(layer => ({
          id: `${layer.id}-${layer.key}`,
          key: layer.key,
          layer,
          isGISSupplementalLayer: false,
          isDisabled: false,
        }))
        .filter(filterPlaceholders);

      setLayerCheckboxes([...gisLayers, ...spatialLayers]);
    } else {
      const spatialLayers = allSpatialLayers
        .map(layer => ({
          id: layer.key,
          key: layer.key,
          layer,
          isGISSupplementalLayer: false,
          isDisabled: false,
        }))
        .filter(filterPlaceholders);
      setLayerCheckboxes([...spatialLayers]);
    }
  }, [
    hasEsriMapAccess,
    gisMapLayersControlServices,
    allSpatialLayers,
    mapProvider,
    isLayerDisabled,
  ]);

  if (
    !shouldRenderMLControls &&
    !shouldRenderEsriMLControls &&
    layerCheckboxes.length < 1
  ) {
    return null;
  }

  return (
    <>
      <Controls>
        <AccordionTitleWrapper onClick={toggleAccordion} horizontal>
          <MapLayerIcon color="primaryText" width={16} height={18} />
          <AccordionTitle>Map Layers</AccordionTitle>
          <CollapsedIconWrapper isCollapsed={isCollapsed}>
            <ChevronUpIcon color="secondaryLink" width={16} height={12} />
          </CollapsedIconWrapper>
        </AccordionTitleWrapper>
        {!isCollapsed && (
          <>
            <AccordionContent>
              {layerCheckboxes.map(cb => (
                <div
                  key={cb.id}
                  onMouseEnter={() => onHandleHoverLayer(cb.id)}
                  onMouseLeave={() => onHandleHoverLayer(null)}
                >
                  <Popover
                    cssDisplayProperty="block"
                    isOpen={cb?.isDisabled ? cb.id === hoveredLayerId : false}
                    popoverID={`map-layers-control-popover-${cb.id}`}
                    trigger={
                      <LayerCheckbox
                        id={cb.id}
                        isDisabled={cb.isDisabled}
                        isGISSupplementalLayer={cb.isGISSupplementalLayer}
                        key={cb.key}
                        layer={cb.layer}
                      />
                    }
                    useCustomOffset
                    xOffset={0}
                    yOffset={24}
                  >
                    <BasemapTooltip />
                  </Popover>
                </div>
              ))}
            </AccordionContent>
            <ControlsBottom>
              <SelectedLayersWrapper>
                <SelectedText>{selectedCheckboxCount} Selected</SelectedText>
                <ClearAllText onClick={handleClearAll}>Clear All</ClearAllText>
              </SelectedLayersWrapper>
            </ControlsBottom>
          </>
        )}
      </Controls>
    </>
  );
};

export default MapLayersControl;
